import React, { useState } from 'react';
import { RxAdjStateBadge } from '../common/RxStatusBadge';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Paper,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  LocalPharmacy as PharmacyIcon,
  Medication as MedicationIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import orderService from '../../services/orderService';
import { formatDateForDisplay } from '../../utils/formatters';
import { usePharmacyStore } from '../../stores/pharmacyStore';

/**
 * Get human-readable status label and color from Kroll Rx.Status code
 * Status codes from Kroll:
 * 1 = Active, 2 = Cancelled, 3 = Unfilled, 4 = Not Dispensed,
 * 5 = Deleted/Void, 6 = On File, 8 = Suspended
 */
const getRxStatusInfo = (status: number): { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' } => {
  switch (status) {
    case 1:
      return { label: 'Active', color: 'success' };
    case 2:
      return { label: 'Cancelled', color: 'error' };
    case 3:
      return { label: 'Unfilled', color: 'warning' };
    case 4:
      return { label: 'Not Dispensed', color: 'warning' };
    case 5:
      return { label: 'Deleted/Void', color: 'error' };
    case 6:
      return { label: 'On File', color: 'info' };
    case 8:
      return { label: 'Suspended', color: 'warning' };
    default:
      return { label: `Status ${status}`, color: 'default' };
  }
};

/**
 * Get drug schedule label (controlled substance classification)
 */
const getDrugScheduleLabel = (schedule: number): string | null => {
  if (!schedule || schedule === 0) return null;
  return `Schedule ${schedule}`;
};

/**
 * Get adherence label with color
 */
const getAdherenceInfo = (days: number | null): { label: string; color: 'success' | 'warning' | 'error' | 'default' } | null => {
  if (days === null || days === undefined) return null;
  if (days === 0) return { label: 'On time', color: 'success' };
  if (days > 0) return { label: `${days} days late`, color: 'warning' };
  return { label: `${Math.abs(days)} days early`, color: 'success' };
};

/**
 * Turn a WorkflowStage enum value ("clinical_verification") into a label.
 */
const formatStage = (stage: string): string =>
  (stage || 'unknown')
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');

/**
 * The slice of an Order we need to offer it as a destination for "Select".
 */
interface MatchedOrder {
  id: string;
  orderNumber?: string;
  workflowStage?: string;
  status?: string;
  deliveryDate?: string | null;
}

/**
 * Result from Rx search containing both Rx and patient info
 */
export interface RxSearchResult {
  // Rx Info
  rxNumber: number;
  origRxNum: number;
  fillDate: string;
  dispQty: number;
  daysSupply: number;
  sig: string;
  status: number;
  rxAdjState?: number | null; // Kroll Rx.AdjState (0/1/5/6/7 = non-clean adjudication; see RxAdjStateBadge)

  // Drug Info
  brandName: string;
  genericName: string;
  drugStrength: string;
  drugDIN: string;
  drugForm: string | null;
  drugSchedule: number;

  // Patient Info
  patientId: number;
  patientFirstName: string;
  patientLastName: string;
  patientName: string;
  patientDOB: string;
  patientPhone: string | null;
  patientAddress: string;
  patientCity: string;
  patientProvince: string;
  patientPostalCode: string;

  // Doctor Info
  doctorName: string;

  // Pricing
  customerPrice: number;
  insurancePays: number;
  retailPrice: number;

  // Enhanced Fields
  autoRefill: boolean;
  writtenDate: string | null;
  rxExpiryDate: string | null;
  followUpDate: string | null;
  inactive: boolean;
  isMistake: boolean;
  partialFill: boolean;
  trialRx: boolean;
  adherenceDays: number | null;
  primaryPlanCode: string | null;
  adjudicationState: number | null;
}

export interface RxSearchDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog closes */
  onClose: () => void;
  /** Callback when an Rx is selected */
  onSelectRx: (result: RxSearchResult) => void;
  /** Optional dialog title */
  title?: string;
  /** Optional subtitle */
  subtitle?: string;
}

/**
 * RxSearchDialog - Universal prescription search component
 *
 * Reusable dialog that searches by Rx number and returns both prescription
 * and patient information. Can be added to any page via a trigger button.
 */
export const RxSearchDialog: React.FC<RxSearchDialogProps> = ({
  open,
  onClose,
  onSelectRx,
  title = 'Search Prescription',
  subtitle = 'Enter Rx number to search',
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { activePharmacy } = usePharmacyStore();

  const [rxNumber, setRxNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RxSearchResult | null>(null);
  // GP-1399: "Select" resolves the Rx to its PHOX order before it can navigate.
  const [resolvingOrder, setResolvingOrder] = useState(false);
  const [orderMatches, setOrderMatches] = useState<MatchedOrder[] | null>(null);

  // Perform search
  const handleSearch = async () => {
    // Validate input
    const trimmed = rxNumber.trim();
    if (!trimmed) {
      setError('Please enter an Rx number');
      return;
    }

    if (trimmed.length < 4) {
      setError('Rx number must be at least 4 digits');
      return;
    }

    if (!activePharmacy?.id) {
      setError('Please select a pharmacy first');
      enqueueSnackbar('Please select a pharmacy before searching', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setOrderMatches(null);

    try {
      // Use existing hardcopy endpoint (same as HardcopyViewer)
      const response = await api.get(`/api/rx/${activePharmacy.id}/hardcopy/${trimmed}`);

      if (response.data.success && response.data.data) {
        const data = response.data.data;

        // Map response to RxSearchResult
        const searchResult: RxSearchResult = {
          // Rx Info
          rxNumber: data.rxNumber,
          origRxNum: data.origRxNum,
          fillDate: data.fillDate,
          dispQty: data.dispQty,
          daysSupply: data.daysSupply,
          sig: data.sig || data.sigTokens || '',
          status: data.status,
          rxAdjState: data.rxAdjState ?? null,

          // Drug Info
          brandName: data.brandName || '',
          genericName: data.genericName || '',
          drugStrength: data.drugStrength || '',
          drugDIN: data.drugDIN || '',
          drugForm: data.drugForm,
          drugSchedule: data.drugSchedule || 0,

          // Patient Info
          patientId: data.patientId,
          patientFirstName: data.patientFirstName || '',
          patientLastName: data.patientLastName || '',
          patientName: data.patientName || `${data.patientLastName}, ${data.patientFirstName}`,
          patientDOB: data.patientDOB || '',
          patientPhone: data.patientPhone,
          patientAddress: data.patientAddress || '',
          patientCity: data.patientCity || '',
          patientProvince: data.patientProvince || '',
          patientPostalCode: data.patientPostalCode || '',

          // Doctor Info
          doctorName: data.doctorName || '',

          // Pricing
          customerPrice: data.customerPrice || 0,
          insurancePays: data.insurancePays || 0,
          retailPrice: data.retailPrice || 0,

          // Enhanced Fields
          autoRefill: !!data.autoRefill,
          writtenDate: data.writtenDate,
          rxExpiryDate: data.rxExpiryDate,
          followUpDate: data.followUpDate,
          inactive: !!data.inactive,
          isMistake: !!data.isMistake,
          partialFill: !!data.partialFill,
          trialRx: !!data.trialRx,
          adherenceDays: data.adherenceDays,
          primaryPlanCode: data.primaryPlanCode,
          adjudicationState: data.adjudicationState,
        };

        setResult(searchResult);
      } else {
        setError(`Rx #${trimmed} not found`);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(`Rx #${trimmed} not found`);
      } else {
        setError(err.message || 'Failed to search. Please try again.');
        console.error('Rx search error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSearch();
    }
  };

  // Open an order's details page — the same destination (and the same route
  // shape) the queue row's "View Order Details" action reaches.
  const openOrder = (order: MatchedOrder, rx: RxSearchResult) => {
    if (!activePharmacy?.id) return;
    onSelectRx(rx);
    handleClose();
    navigate(`/orders/${activePharmacy.id}/${order.id}`);
  };

  /**
   * Handle select (GP-1399)
   *
   * "Select" used to only fire onSelectRx and close, so from the queues it read
   * as a dead button: the dialog vanished and nothing happened. It now resolves
   * the Rx to its PHOX order and navigates to that order's details.
   *
   * The hardcopy lookup only carries Kroll-side fields (Rx number, patient id) —
   * never the order UUID the /orders/:pharmacyId/:orderId route needs — so the
   * order has to be resolved through the by-rx-list endpoint. That endpoint can
   * legitimately return zero rows (Rx exists in Kroll but was never billed into
   * PHOX) or several (e.g. a cancelled fill plus its rebill), and we never guess:
   * zero says so and keeps the dialog open, many make the user pick.
   */
  const handleSelect = async () => {
    if (!result || !activePharmacy?.id) return;

    setResolvingOrder(true);
    setError(null);
    setOrderMatches(null);

    try {
      const orders: MatchedOrder[] = await orderService.getOrdersByRxNumber(
        activePharmacy.id,
        String(result.rxNumber),
      );

      if (!orders || orders.length === 0) {
        setError(
          `Rx #${result.rxNumber} has no order in ${activePharmacy.name} — there is nothing to open. ` +
            `Use "View Patient" to see this patient's profile.`,
        );
        return;
      }

      if (orders.length === 1) {
        openOrder(orders[0], result);
        return;
      }

      setOrderMatches(orders);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        `Could not look up the order for Rx #${result.rxNumber}. Please try again.`;
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
      console.error('Rx → order lookup failed:', err);
    } finally {
      setResolvingOrder(false);
    }
  };

  // Handle View Patient
  const handleViewPatient = () => {
    if (result && activePharmacy) {
      // Construct composite patient ID
      const compositePatientId = `${activePharmacy.id}_${result.patientId}`;
      navigate(`/patients/${compositePatientId}`);
      handleClose();
    }
  };

  // Reset and close
  const handleClose = () => {
    setRxNumber('');
    setResult(null);
    setError(null);
    setOrderMatches(null);
    setResolvingOrder(false);
    onClose();
  };

  // Format date
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'N/A';
    try {
      // Strip timezone suffix to treat as local time
      const cleanDateStr = dateStr.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
      const date = new Date(cleanDateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `$${amount.toFixed(2)}`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '50vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" component="div">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
            {activePharmacy && (
              <Chip
                icon={<PharmacyIcon />}
                label={`Active: ${activePharmacy.name}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label="Close dialog" title="Close">
            <CloseIcon aria-hidden="true" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Search Input */}
        <Box sx={{ mt: 2, mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Rx Number"
            placeholder="Enter prescription number"
            value={rxNumber}
            onChange={(e) => setRxNumber(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={!activePharmacy || loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MedicationIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSearch}
                    disabled={!activePharmacy || loading || !rxNumber.trim()}
                    color="primary"
                    aria-label="Search"
                    title="Search"
                  >
                    {loading ? <CircularProgress size={24} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            autoFocus
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Press Enter or click the search icon to search
          </Typography>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Result Card */}
        {result && (
          <Paper elevation={2} sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Rx #{result.rxNumber}
                </Typography>
                {result.autoRefill && (
                  <Chip label="Auto-Refill" size="small" color="info" variant="outlined" />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {result.inactive && (
                  <Chip label="Inactive" size="small" color="error" />
                )}
                {result.isMistake && (
                  <Chip label="Mistake" size="small" color="error" />
                )}
                {result.partialFill && (
                  <Chip label="Partial" size="small" color="warning" variant="outlined" />
                )}
                {result.trialRx && (
                  <Chip label="Trial" size="small" color="info" variant="outlined" />
                )}
                {/* Kroll adjudication flag: a stuck claim leaves Status 1 (Active),
                    so the status chip alone hides "Pending Adj"/"Needs Review" fills. */}
                <RxAdjStateBadge adjState={result.rxAdjState} size="small" />
                <Chip
                  label={`${getRxStatusInfo(result.status).label} (${result.status})`}
                  size="small"
                  color={getRxStatusInfo(result.status).color}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Patient Info */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  Patient
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {result.patientName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    DOB: {formatDate(result.patientDOB)}
                  </Typography>
                </Box>
                {result.patientPhone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {result.patientPhone}
                    </Typography>
                  </Box>
                )}
              </Box>
              {result.patientAddress && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {result.patientAddress}, {result.patientCity}, {result.patientProvince} {result.patientPostalCode}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Medication Info */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MedicationIcon color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  Medication
                </Typography>
                {getDrugScheduleLabel(result.drugSchedule) && (
                  <Chip
                    label={getDrugScheduleLabel(result.drugSchedule)}
                    size="small"
                    color="warning"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {result.brandName || result.genericName} {result.drugStrength}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                DIN: {result.drugDIN} {result.drugForm && `| ${result.drugForm}`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={`Qty: ${result.dispQty}`} size="small" variant="outlined" />
                <Chip label={`Days: ${result.daysSupply}`} size="small" variant="outlined" />
                <Chip label={`Filled: ${formatDate(result.fillDate)}`} size="small" variant="outlined" />
                {getAdherenceInfo(result.adherenceDays) && (
                  <Chip
                    label={getAdherenceInfo(result.adherenceDays)!.label}
                    size="small"
                    color={getAdherenceInfo(result.adherenceDays)!.color}
                  />
                )}
              </Box>
              {result.sig && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Sig: {result.sig}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Pricing */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Pricing
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(result.retailPrice)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Insurance
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="success.main">
                    {formatCurrency(result.insurancePays)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Patient Pays
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="primary.main">
                    {formatCurrency(result.customerPrice)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Important Dates & Insurance */}
            {(result.writtenDate || result.rxExpiryDate || result.followUpDate || result.primaryPlanCode) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {result.writtenDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Written
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(result.writtenDate)}
                      </Typography>
                    </Box>
                  )}
                  {result.rxExpiryDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Rx Expires
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(result.rxExpiryDate)}
                      </Typography>
                    </Box>
                  )}
                  {result.followUpDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Next Fill Due
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(result.followUpDate)}
                      </Typography>
                    </Box>
                  )}
                  {result.primaryPlanCode && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Primary Plan
                      </Typography>
                      <Typography variant="body2">
                        {result.primaryPlanCode}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Doctor */}
            {result.doctorName && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Prescriber: {result.doctorName}
                </Typography>
              </>
            )}
          </Paper>
        )}

        {/* Order picker — only when one Rx maps to several orders (GP-1399).
            Picking for the user would be a silent guess, so they choose. */}
        {result && orderMatches && orderMatches.length > 1 && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Rx #{result.rxNumber} is on {orderMatches.length} orders — choose the one to open
            </Typography>
            <List dense disablePadding>
              {orderMatches.map((order) => (
                <ListItemButton
                  key={order.id}
                  onClick={() => openOrder(order, result)}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}
                >
                  <ListItemText
                    primary={order.orderNumber || order.id}
                    secondary={[
                      formatStage(order.workflowStage || ''),
                      order.status,
                      order.deliveryDate
                        ? `Delivery ${formatDateForDisplay(order.deliveryDate)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}

        {/* Empty State */}
        {!loading && !result && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <MedicationIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Search for a Prescription
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter an Rx number and press Enter or click the search icon
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        {result && (
          <>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={handleViewPatient}
            >
              View Patient
            </Button>
            <Button
              variant="contained"
              onClick={handleSelect}
              disabled={resolvingOrder}
              startIcon={resolvingOrder ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Select
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RxSearchDialog;
